import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  getSaasDashboard, getOrganizations, createOrganization, updateOrganization,
  exportShipmentsFull, importShipmentsFull,
  deactivateOrganization, reactivateOrganization, getPlans,
  inviteUser, getOrgUsers, removeOrgUser
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useI18n } from '../contexts/I18nContext';
import { toast } from 'sonner';
import {
  Shield, Building2, Plus, Users, Package, Power,
  Loader2, Search, UserPlus, Trash2, Ship, BarChart3,
  CreditCard, Settings, Mail, ChevronRight, Eye, DollarSign, TrendingUp, Download, Upload, Crown
} from 'lucide-react';
import axios from 'axios';
import { SyncHealthDashboard } from '../components/SyncHealthDashboard';
import { EmailDiagnosticsCard } from '../components/EmailDiagnosticsCard';
import ScraperHealthCard from '../components/ScraperHealthCard';
import { AlertRecipientsCard } from '../components/AlertRecipientsCard';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_FEATURE_LABELS = {
  shipments: 'Embarques',
  containers: 'Contenedores',
  events: 'Eventos',
  internal_notes: 'Notas Internas',
  dashboard: 'Dashboard',
  client_portal: 'Portal Cliente',
  tracking_ports: 'Tracking Puertos',
  tracking_carriers: 'Tracking Navieras',
  document_generation: 'Generacion Docs',
  cost_breakdown: 'Costos',
  certificates: 'Certificados',
  alerts: 'Alertas',
  monthly_report: 'Reporte Mensual',
  sla_dashboard: 'Dashboard SLA',
  multi_user: 'Multi-Usuario',
  excel_export: 'Exportar Excel',
  audit_history: 'Historial',
  client_arnulfo: 'Dashboard Arnulfo (Cliente)',
  client_force_soia: 'Forzar SOIA (Cliente)',
};

const ORG_TYPE_LABELS = {
  comercializadora: { label: 'Comercializadora', color: 'bg-blue-500/20 text-blue-400' },
  agente_aduanal: { label: 'Agente Aduanal', color: 'bg-purple-500/20 text-purple-400' },
  cliente: { label: 'Cliente', color: 'bg-green-500/20 text-green-400' },
};

function StatCard({ icon: Icon, label, value, sub, color = "text-primary" }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 ${color}`}>
          <Icon size={20} />
        </div>
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function SuperAdminPage() {
  const { user, enterViewAs } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [admins, setAdmins] = useState([]);
  const [showAdminsSection, setShowAdminsSection] = useState(false);
  // Dialogs
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', org_type: 'comercializadora', plan_code: 'COMERCIALIZADORA_BASIC', contact_email: '' });
  const [showInvite, setShowInvite] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'ops' });
  const [managingOrg, setManagingOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('ofimex_token');
      const [statsRes, plansRes] = await Promise.all([
        axios.get(`${API}/api/saas-dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/plans`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setPlans(plansRes.data);
    } catch (err) {
      console.error('Error loading SaaS data:', err);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAdmins = useCallback(async () => {
    try {
      const token = localStorage.getItem('ofimex_token');
      const res = await axios.get(`${API}/api/super-admin/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmins(res.data);
    } catch (err) {
      toast.error('Error cargando lista de Super Admins');
    }
  }, []);

  useEffect(() => {
    if (showAdminsSection) loadAdmins();
  }, [showAdminsSection, loadAdmins]);

  // Load admin count once on mount
  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', password: '' });
  const handleCreateSuperAdmin = async () => {
    const { email, name, password } = newAdmin;
    if (!email.trim() || !name.trim() || password.length < 8) {
      toast.error('Email, nombre y password (mínimo 8 caracteres) son requeridos');
      return;
    }
    try {
      const token = localStorage.getItem('ofimex_token');
      await axios.post(`${API}/api/super-admin/admins`, { email, name, password }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Super Admin ${email} creado`);
      setNewAdmin({ email: '', name: '', password: '' });
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear Super Admin');
    }
  };

  const handleTransferMaster = async (admin) => {
    if (!window.confirm(
      `¿Transferir el rol de Master Admin a ${admin.email}?\n\n` +
      `Tu cuenta actual (${user?.email}) seguirá siendo Super Admin pero perderá los privilegios de Master ` +
      `(crear/eliminar Super Admins, transferir el master, etc.). Solo el Master puede hacer esta acción.`
    )) return;
    try {
      const token = localStorage.getItem('ofimex_token');
      await axios.post(`${API}/api/super-admin/admins/${admin.id}/transfer-master`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Master Admin transferido a ${admin.email}. Cierra sesión y vuelve a entrar para ver el cambio.`);
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al transferir');
    }
  };

  const handleRevoke = async (admin) => {
    if (admin.is_master_admin) {
      toast.error('No puedes revocar al Master Admin');
      return;
    }
    if (!window.confirm(`¿Revocar Super Admin a ${admin.email}?`)) return;
    try {
      const token = localStorage.getItem('ofimex_token');
      await axios.post(`${API}/api/super-admin/admins/${admin.id}/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Acceso revocado a ${admin.email}`);
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al revocar');
    }
  };

  const handleCreateOrg = async () => {
    try {
      await createOrganization(newOrg);
      toast.success('Organizacion creada');
      setShowNewOrg(false);
      setNewOrg({ name: '', org_type: 'comercializadora', plan_code: 'COMERCIALIZADORA_BASIC', contact_email: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear');
    }
  };

  const handleInvite = async () => {
    try {
      const res = await inviteUser(showInvite.id, inviteForm);
      toast.success(`Usuario invitado. Contrasena temporal: ${res.data.temp_password}`, { duration: 15000 });
      setShowInvite(null);
      setInviteForm({ email: '', name: '', role: 'ops' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al invitar');
    }
  };

  const handleManageOrg = async (org) => {
    setManagingOrg(org);
    try {
      const res = await getOrgUsers(org.id);
      setOrgUsers(res.data);
    } catch {
      setOrgUsers([]);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!managingOrg) return;
    if (!window.confirm('Eliminar este usuario?')) return;
    try {
      await removeOrgUser(managingOrg.id, userId);
      toast.success('Usuario eliminado');
      const res = await getOrgUsers(managingOrg.id);
      setOrgUsers(res.data);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleExportFull = async () => {
    const ok = window.confirm('Exportar TODAS las cargas pendientes de entrega como archivo JSON?\n\nEste archivo incluye embarques, contenedores, tracking, eventos, notas. Úsalo para migrar a otro entorno (prod → preview).');
    if (!ok) return;
    const tid = toast.loading('Exportando...');
    try {
      const res = await exportShipmentsFull(false);
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ofimex_shipments_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${data.count} embarques exportados`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error exportando', { id: tid });
    }
  };

  const handleImportFull = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const overwrite = window.confirm('¿Sobreescribir embarques/contenedores ya existentes?\n\nOK = sí (actualiza si ya existen)\nCancelar = no (solo crea los que faltan)');
    const tid = toast.loading('Importando bundle...');
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const res = await importShipmentsFull(bundle, overwrite);
      const s = res.data.summary || {};
      toast.success(
        `Embarques: ${s.shipments_created} creados, ${s.shipments_updated} updated, ${s.shipments_skipped} skipped · Contenedores: ${s.containers_created} creados`,
        { id: tid, duration: 10000 }
      );
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error importando: ' + (err.message || ''), { id: tid });
    }
  };

  const handleImpersonate = async (targetUser) => {
    // Use View-As pattern: super-admin keeps identity, scopes to target user's org
    try {
      const orgId = targetUser.organization_id;
      if (!orgId) {
        toast.error('Este usuario no pertenece a una organización');
        return;
      }
      await enterViewAs(orgId);
      toast.success(`Entrando como Super Admin a la org de ${targetUser.email}...`);
      window.location.href = targetUser.role === 'client' ? '/my-dashboard' : '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al entrar a la organización');
    }
  };

  const handleEnterOrgPortal = async (org) => {
    try {
      await enterViewAs(org.id);
      toast.success(`Entrando como Super Admin a ${org.name}...`);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al acceder al portal');
    }
  };

  const handleToggleOrg = async (org) => {
    const action = org.is_active ? 'desactivar' : 'reactivar';
    if (!window.confirm(`${action} ${org.name}?`)) return;
    try {
      if (org.is_active) {
        await deactivateOrganization(org.id, { reason: 'admin_action' });
      } else {
        await reactivateOrganization(org.id);
      }
      toast.success(`Organizacion ${action}da`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`ELIMINAR ${org.name}? Esta accion no se puede deshacer.`)) return;
    if (!window.confirm(`Confirmar eliminacion de ${org.name} y TODOS sus datos?`)) return;
    try {
      const token = localStorage.getItem('ofimex_token');
      await axios.delete(`${API}/api/organizations/${org.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Organizacion eliminada');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleEditOrg = async (org) => {
    setEditingOrg(org);
    setEditForm({
      name: org.name || '',
      plan_code: org.plan_code || '',
      max_operators: org.max_operators || 2,
      max_clients: org.max_clients || 5,
      billing_cycle: org.billing_cycle || 'monthly',
      next_payment_date: org.next_payment_date || '',
      contact_email: org.contact_email || '',
      notes: org.notes || '',
      monthly_price_override: org.monthly_price_override ?? '',
      // Use effective_features (plan + custom overrides) for the toggle display
      custom_features: { ...(org.effective_features || {}) },
    });
  };

  const handleSaveEdit = async () => {
    if (!editingOrg) return;
    try {
      const priceOverrideRaw = editForm.monthly_price_override;
      const priceOverride = priceOverrideRaw === '' || priceOverrideRaw === null || priceOverrideRaw === undefined
        ? null
        : parseFloat(priceOverrideRaw);
      await updateOrganization(editingOrg.id, {
        name: editForm.name,
        plan_code: editForm.plan_code,
        max_operators: parseInt(editForm.max_operators),
        max_clients: parseInt(editForm.max_clients),
        billing_cycle: editForm.billing_cycle,
        next_payment_date: editForm.next_payment_date || null,
        contact_email: editForm.contact_email,
        notes: editForm.notes,
        monthly_price_override: priceOverride,
        custom_features: editForm.custom_features,
      });
      toast.success('Organizacion actualizada');
      setEditingOrg(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const toggleFeature = (code) => {
    setEditForm(prev => ({
      ...prev,
      custom_features: {
        ...prev.custom_features,
        [code]: !prev.custom_features[code],
      }
    }));
  };

  const filteredOrgs = (stats?.organizations || []).filter(o =>
    !search || o.name.toLowerCase().includes(search.toLowerCase()) || (o.contact_email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" size={32} /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="super-admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Shield size={28} /> OFIMEX Admin
            </h1>
            <p className="text-muted-foreground mt-1">{t('superAdmin.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAdminsSection(true)}
              data-testid="manage-super-admins-btn"
            >
              <Shield size={16} className="mr-2" />
              Super Admins ({admins.length || '...'})
            </Button>
            <Button
              variant="outline"
              onClick={handleExportFull}
              title={t('superAdmin.exportTitle')}
              data-testid="export-full-btn"
            >
              <Download size={16} className="mr-2" />
              {t('superAdmin.exportShipments')}
            </Button>
            <input
              type="file"
              accept=".json"
              id="import-full-file"
              className="hidden"
              onChange={handleImportFull}
              data-testid="import-full-input"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('import-full-file')?.click()}
              title={t('superAdmin.importTitle')}
              data-testid="import-full-btn"
            >
              <Upload size={16} className="mr-2" />
              {t('superAdmin.importJson')}
            </Button>
          <Dialog open={showNewOrg} onOpenChange={setShowNewOrg}>
            <DialogTrigger asChild>
              <Button data-testid="new-org-btn"><Plus size={16} className="mr-2" />{t('superAdmin.newClient')}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('superAdmin.newClientTitle')}</DialogTitle>
                <DialogDescription>{t('superAdmin.subtitle')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t('superAdmin.name')}</Label>
                  <Input value={newOrg.name} onChange={(e) => setNewOrg({...newOrg, name: e.target.value})} placeholder={t('superAdmin.namePh')} />
                </div>
                <div>
                  <Label>{t('superAdmin.type')}</Label>
                  <Select value={newOrg.org_type} onValueChange={(v) => setNewOrg({...newOrg, org_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comercializadora">{t('superAdmin.typeComercializadora')}</SelectItem>
                      <SelectItem value="agente_aduanal">{t('superAdmin.typeAgenteAduanal')}</SelectItem>
                      <SelectItem value="cliente">{t('superAdmin.typeCliente')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('superAdmin.plan')}</Label>
                  <Select value={newOrg.plan_code} onValueChange={(v) => setNewOrg({...newOrg, plan_code: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {plans.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('superAdmin.contactEmail')}</Label>
                  <Input value={newOrg.contact_email} onChange={(e) => setNewOrg({...newOrg, contact_email: e.target.value})} placeholder="email@empresa.com" />
                </div>
                <Button onClick={handleCreateOrg} className="w-full">{t('superAdmin.create')}</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard icon={DollarSign} label={t('superAdmin.statsMrr')} value={`$${(stats?.mrr_active || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`} sub={`${stats?.active_orgs || 0} ${t('superAdmin.statsActiveOrgs').toLowerCase()}`} color="text-emerald-400" />
          <StatCard icon={TrendingUp} label={t('superAdmin.statsArr')} value={`$${(stats?.arr_active || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`} sub="12 × MRR" color="text-emerald-300" />
          <StatCard icon={Building2} label={t('superAdmin.clients')} value={stats?.active_orgs || 0} sub={`${stats?.total_orgs || 0} ${t('superAdmin.overall').toLowerCase()}`} color="text-blue-400" />
          <StatCard icon={Users} label={t('superAdmin.users')} value={stats?.active_users || 0} sub={`${stats?.total_users || 0}`} color="text-green-400" />
          <StatCard icon={Ship} label={t('superAdmin.shipments')} value={stats?.total_shipments || 0} color="text-amber-400" />
          <StatCard icon={Package} label={t('superAdmin.statsTotalContainers')} value={stats?.total_containers || 0} color="text-purple-400" />
        </div>

        {/* Organizations Table */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t('superAdmin.clients')}</h2>
            <div className="relative w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('superAdmin.searchPh')} className="pl-9" data-testid="search-orgs" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground uppercase">
                  <th className="py-2 px-4 text-left">{t('superAdmin.organization')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.type')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.plan')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.pricePerMonth')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.state')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.users')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.shipments')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.contact')}</th>
                  <th className="py-2 px-4 text-left">{t('superAdmin.actionsCol')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrgs.length === 0 ? (
                  <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">{t('superAdmin.noClients')}</td></tr>
                ) : (
                  filteredOrgs.map(org => {
                    const typeInfo = ORG_TYPE_LABELS[org.org_type] || ORG_TYPE_LABELS.comercializadora;
                    const typeLabelKey = org.org_type === 'agente_aduanal' ? 'superAdmin.typeAgenteAduanal' : (org.org_type === 'cliente' ? 'superAdmin.typeCliente' : 'superAdmin.typeComercializadora');
                    return (
                      <tr key={org.id} className="border-b border-border hover:bg-card/50" data-testid={`org-row-${org.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Building2 size={16} className="text-muted-foreground" />
                            <span className="font-medium">{org.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={typeInfo.color}>{t(typeLabelKey)}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">{org.plan_code?.replace(/_/g, ' ')}</td>
                        <td className="py-3 px-4 text-sm" data-testid={`org-price-${org.id}`}>
                          <span className="font-mono text-emerald-400">${(org.monthly_price ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                          {org.monthly_price_override !== null && org.monthly_price_override !== undefined && (
                            <span className="ml-1 text-xs text-amber-400" title={t('superAdmin.customOverride')}>⚙️</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={org.is_active ? "default" : "destructive"}>
                            {org.is_active ? t('superAdmin.statusActive') : t('superAdmin.statusInactive')}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span className="text-blue-400">{org.operators}</span>/{org.max_operators} ops
                          <span className="mx-1 text-muted-foreground">|</span>
                          <span className="text-green-400">{org.clients}</span>/{org.max_clients} cli
                        </td>
                        <td className="py-3 px-4 text-sm font-mono">{org.shipments}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{org.contact_email || '—'}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button variant="default" size="sm" onClick={() => handleEnterOrgPortal(org)} title={t('superAdmin.enterPortal')} data-testid={`enter-org-${org.id}`} className="bg-primary hover:bg-primary/80">
                              <Eye size={14} className="mr-1" /> {t('superAdmin.confirmEnter')}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEditOrg(org)} title={t('superAdmin.edit')} data-testid={`edit-org-${org.id}`}>
                              <Settings size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleManageOrg(org)} title={t('superAdmin.users')} data-testid={`manage-org-${org.id}`}>
                              <Users size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setShowInvite(org)} title={t('superAdmin.invite')} data-testid={`invite-org-${org.id}`}>
                              <UserPlus size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleToggleOrg(org)} title={org.is_active ? t('superAdmin.deactivate') : t('superAdmin.activate')}>
                              <Power size={14} className={org.is_active ? 'text-green-400' : 'text-red-400'} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteOrg(org)} title={t('superAdmin.delete')} className="text-destructive hover:text-destructive">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sync Health Dashboard */}
        <div className="bg-card border border-border rounded-lg p-6">
          <SyncHealthDashboard />
        </div>

        {/* Tracking Scraper Health (carrier, port, ferrovalle, soia) */}
        <ScraperHealthCard />

        {/* Notification recipients for system alerts */}
        <AlertRecipientsCard />

        {/* Email Diagnostics */}
        <EmailDiagnosticsCard />

        {/* Edit Org Dialog */}
        <Dialog open={!!editingOrg} onOpenChange={() => setEditingOrg(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('superAdmin.editOrgTitle')}: {editingOrg?.name}</DialogTitle>
              <DialogDescription>{t('superAdmin.subtitle')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('superAdmin.nameField')}</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
                </div>
                <div>
                  <Label>{t('superAdmin.emailContact')}</Label>
                  <Input value={editForm.contact_email} onChange={(e) => setEditForm({...editForm, contact_email: e.target.value})} />
                </div>
              </div>

              {/* Subscription */}
              <div>
                <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">{t('superAdmin.subscription')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>{t('superAdmin.plan')}</Label>
                    <Select value={editForm.plan_code} onValueChange={(v) => setEditForm({...editForm, plan_code: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {plans.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('superAdmin.cycle')}</Label>
                    <Select value={editForm.billing_cycle} onValueChange={(v) => setEditForm({...editForm, billing_cycle: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">{t('superAdmin.monthly')}</SelectItem>
                        <SelectItem value="yearly">{t('superAdmin.yearly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('superAdmin.maxOperators')}</Label>
                    <Input type="number" value={editForm.max_operators} onChange={(e) => setEditForm({...editForm, max_operators: e.target.value})} />
                  </div>
                  <div>
                    <Label>{t('superAdmin.maxClients')}</Label>
                    <Input type="number" value={editForm.max_clients} onChange={(e) => setEditForm({...editForm, max_clients: e.target.value})} />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('superAdmin.nextPayment')}</Label>
                    <Input type="date" value={editForm.next_payment_date?.slice(0, 10) || ''} onChange={(e) => setEditForm({...editForm, next_payment_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>{t('superAdmin.monthlyPriceOverride')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder={`Default plan: $${(editingOrg?.plan_price_monthly ?? 0).toLocaleString()}`}
                      value={editForm.monthly_price_override}
                      onChange={(e) => setEditForm({...editForm, monthly_price_override: e.target.value})}
                      data-testid="monthly-price-override-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('superAdmin.overrideHelp')} ({editingOrg?.plan_code || '—'})</p>
                  </div>
                </div>
              </div>

              {/* Features Toggle */}
              <div>
                <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">{t('superAdmin.featuresActive')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.keys(DEFAULT_FEATURE_LABELS).map((code) => (
                    <div key={code} className="flex items-center justify-between p-2 bg-secondary/30 rounded" data-testid={`feature-toggle-${code}`}>
                      <span className="text-sm">{t(`superAdmin.featLabel.${code}`)}</span>
                      <Switch
                        checked={editForm.custom_features?.[code] ?? false}
                        onCheckedChange={() => toggleFeature(code)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>{t('superAdmin.internalNotes')}</Label>
                <Input value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} placeholder={t('superAdmin.notesPh')} />
              </div>

              <Button onClick={handleSaveEdit} className="w-full" data-testid="save-org-btn">{t('superAdmin.saveChanges')}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite User Dialog */}
        <Dialog open={!!showInvite} onOpenChange={() => setShowInvite(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('superAdmin.inviteUserTo')} {showInvite?.name}</DialogTitle>
              <DialogDescription>{t('superAdmin.subtitle')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('superAdmin.emailLabel')}</Label>
                <Input value={inviteForm.email} onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})} placeholder={t('superAdmin.emailPh')} data-testid="invite-email" />
              </div>
              <div>
                <Label>{t('superAdmin.nameField')}</Label>
                <Input value={inviteForm.name} onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})} placeholder={t('superAdmin.fullNamePh')} data-testid="invite-name" />
              </div>
              <div>
                <Label>{t('superAdmin.roleLabel')}</Label>
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({...inviteForm, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ops">{t('superAdmin.roleOps')}</SelectItem>
                    <SelectItem value="client">{t('superAdmin.roleClient')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleInvite} className="w-full" data-testid="send-invite-btn">
                <UserPlus size={16} className="mr-2" /> {t('superAdmin.sendInvitation')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manage Users Dialog */}
        <Dialog open={!!managingOrg} onOpenChange={() => setManagingOrg(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('superAdmin.usersOf')} {managingOrg?.name}</DialogTitle>
              <DialogDescription>{t('superAdmin.subtitle')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {orgUsers.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">{t('superAdmin.noUsers')}</p>
              ) : (
                orgUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded" data-testid={`user-row-${u.id}`}>
                    <div>
                      <p className="text-sm font-medium">{u.name || u.email}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {u.role === 'admin' ? t('superAdmin.roleAdmin') : u.role === 'ops' ? t('superAdmin.roleOps') : t('superAdmin.roleClient')}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${u.is_active ? 'text-green-400' : 'text-red-400'}`}>
                        {u.is_active ? t('superAdmin.activeUser') : t('superAdmin.inactiveUser')}
                      </Badge>
                      <Button variant="outline" size="sm" onClick={() => handleImpersonate(u)} title={t('superAdmin.impersonateTitle')} data-testid={`impersonate-${u.id}`}>
                        <Eye size={14} className="mr-1" /> {t('superAdmin.confirmEnter')}
                      </Button>
                      {!u.is_master_admin && !u.is_super_admin && (
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveUser(u.id)} className="text-destructive">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <Button onClick={() => setShowInvite(managingOrg)} variant="outline" className="w-full mt-2">
              <UserPlus size={16} className="mr-2" /> {t('superAdmin.inviteUserBtn')}
            </Button>
          </DialogContent>
        </Dialog>

        {/* Super Admins management dialog */}
        <Dialog open={showAdminsSection} onOpenChange={setShowAdminsSection}>
          <DialogContent className="max-w-2xl" data-testid="super-admins-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield size={18} /> Super Admins de OFIMEX
              </DialogTitle>
              <DialogDescription>
                Operadores de OFIMEX con acceso global al panel SaaS. Son cuentas independientes
                de cualquier organización cliente — usan emails internos del staff.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 mt-2 max-h-[420px] overflow-y-auto">
              {admins.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between p-3 border border-border rounded-lg"
                  data-testid={`admin-row-${a.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{a.email}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-muted-foreground">{a.name}</span>
                      {a.is_master_admin && (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Master</Badge>
                      )}
                      {a.is_super_admin && !a.is_master_admin && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Super</Badge>
                      )}
                      {!a.is_active && <Badge variant="secondary">Inactivo</Badge>}
                      {(() => {
                        if (!a.last_login_at) {
                          return <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30" title="Nunca ha iniciado sesion">Nunca conectado</Badge>;
                        }
                        const now = Date.now();
                        const last = new Date(a.last_login_at).getTime();
                        const days = Math.floor((now - last) / (1000 * 60 * 60 * 24));
                        const dt = new Date(a.last_login_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                        if (days <= 7) {
                          return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30" title={`Ultimo acceso: ${dt}`}>Activo · {days === 0 ? 'hoy' : `${days}d`}</Badge>;
                        }
                        if (days <= 30) {
                          return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30" title={`Ultimo acceso: ${dt}`}>Hace {days}d</Badge>;
                        }
                        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30" title={`Ultimo acceso: ${dt}`}>Inactivo · {days}d</Badge>;
                      })()}
                    </div>
                  </div>
                  {!a.is_master_admin && user?.is_master_admin && (
                    <div className="flex gap-1 flex-shrink-0">
                      {a.is_super_admin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTransferMaster(a)}
                          data-testid={`transfer-master-${a.id}`}
                          title="Hacer a este Super Admin el Master"
                        >
                          <Crown size={14} className="mr-1" /> Hacer Master
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevoke(a)}
                        data-testid={`revoke-admin-${a.id}`}
                      >
                        <Trash2 size={14} className="mr-1" /> Revocar
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {user?.is_master_admin && (
              <div className="border-t border-border pt-4 mt-4 space-y-3">
                <Label className="text-sm font-medium">Crear nuevo Super Admin (staff de OFIMEX)</Label>
                <p className="text-xs text-muted-foreground">
                  Esta cuenta NO pertenece a ninguna organización cliente. Es para operadores internos de OFIMEX.
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input
                    type="email"
                    placeholder="email@ofimex.com"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    data-testid="new-admin-email"
                  />
                  <Input
                    type="text"
                    placeholder="Nombre completo"
                    value={newAdmin.name}
                    onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                    data-testid="new-admin-name"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="Contraseña (min. 8 caracteres)"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    data-testid="new-admin-password"
                  />
                  <Button onClick={handleCreateSuperAdmin} data-testid="create-admin-btn">
                    Crear
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
